/* W17-B2 — Temporal Diff : l'organisation comparée entre T0 et Tn.
   Quoi / quand / preuve / qu'est-ce que ça affecte — classification stricte :
   observed_change · known_relation · probable_impact · demonstrated_causality. */
import { useEffect, useState } from "react";
import { GitCompare, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { fmtError, notify } from "@/lib/notify";

const CLS = {
  demonstrated_causality: { label: "causalité démontrée", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10" },
  known_relation: { label: "relation connue", cls: "text-sky-300 border-sky-400/40 bg-sky-500/10" },
  probable_impact: { label: "impact probable", cls: "text-amber-300 border-amber-400/40 bg-amber-500/10" },
};
const KIND_LABEL = { node_added: "Ajouté", node_removed: "Retiré", state_changed: "État modifié" };

export const TemporalDiffPanel = ({ onClose, onOpenNode }) => {
  const defFrom = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const run = async (f = from, t = to) => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/org-model/diff?date_from=${f}${t ? `&date_to=${t}` : ""}`);
      setData(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { run(defFrom, ""); }, []); // eslint-disable-line

  const s = data?.summary;
  return (
    <div className="absolute top-[128px] right-3 bottom-3 w-[430px] z-40 noam-card overflow-hidden flex flex-col" data-testid="temporal-diff-panel">
      <div className="p-3 border-b border-white/10 flex items-center gap-2">
        <GitCompare className="w-4 h-4 text-[#EF9F27]" />
        <div className="text-sm font-semibold text-white flex-1">Diff temporel — ce qui a changé</div>
        <button onClick={onClose} data-testid="temporal-diff-close" className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-3 border-b border-white/5 flex items-center gap-2 text-[10px]">
        <span className="text-zinc-500">De</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="temporal-diff-from"
          className="bg-black/30 border border-white/10 rounded px-1.5 py-1 text-zinc-200 [color-scheme:dark]" />
        <span className="text-zinc-500">à</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="temporal-diff-to"
          className="bg-black/30 border border-white/10 rounded px-1.5 py-1 text-zinc-200 [color-scheme:dark]" />
        <button onClick={() => run()} data-testid="temporal-diff-run"
          className="ml-auto px-2.5 py-1 rounded bg-[#EF9F27] text-black font-medium">Comparer</button>
      </div>
      {loading && <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#EF9F27]" /></div>}
      {!loading && data && (
        <div className="flex-1 overflow-y-auto">
          <div className="p-3 grid grid-cols-3 gap-2 text-center" data-testid="temporal-diff-summary">
            {[["+ nœuds", s.nodes_added, "text-emerald-300"], ["− nœuds", s.nodes_removed, "text-red-300"],
              ["états", s.states_changed, "text-amber-300"], ["+ relations", s.relations_added, "text-sky-300"],
              ["− relations", s.relations_removed, "text-red-300"], ["événements", s.journalized_events_in_window, "text-zinc-300"]].map(([l, v, c]) => (
              <div key={l} className="rounded border border-white/5 bg-white/[0.02] py-1.5">
                <div className={`text-sm font-semibold ${c}`}>{v}</div>
                <div className="text-[9px] text-zinc-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="px-3 pb-1 text-[9px] text-zinc-500">{data.doctrine}</div>
          {(data.changes || []).slice(0, 80).map((c, i) => (
            <div key={i} className="mx-3 mb-2 rounded border border-white/5 bg-white/[0.02]" data-testid={`temporal-diff-change-${i}`}>
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-left px-2.5 py-2 flex items-center gap-2">
                <span className={`text-[9px] px-1.5 py-0.5 rounded border ${c.kind === "node_removed" ? "border-red-400/40 text-red-300" : c.kind === "state_changed" ? "border-amber-400/40 text-amber-300" : "border-emerald-400/40 text-emerald-300"}`}>
                  {KIND_LABEL[c.kind]}</span>
                <span className="text-[11px] text-white truncate flex-1">{c.label}</span>
                <span className="text-[9px] text-zinc-500">{c.type}</span>
              </button>
              {open === i && (
                <div className="px-2.5 pb-2 space-y-1.5 text-[10px]">
                  {c.kind === "state_changed" && (
                    <div className="text-zinc-400">État : <span className="text-zinc-200">{c.from_state}</span> → <span className="text-white">{c.to_state}</span></div>
                  )}
                  <div className="text-zinc-500">Fenêtre : après {c.window?.after} · jusqu'à {c.window?.until}</div>
                  {(c.evidence?.events || []).length > 0 && (
                    <div>
                      <div className="text-zinc-400 mb-0.5">Preuves (événements journalisés) :</div>
                      {c.evidence.events.slice(0, 4).map((ev, j) => (
                        <div key={j} className="text-zinc-500">· {String(ev.at || "").slice(0, 16)} — {ev.type}{ev.by ? ` — ${ev.by}` : ""}</div>
                      ))}
                    </div>
                  )}
                  {(c.affects || []).length > 0 && (
                    <div>
                      <div className="text-zinc-400 mb-0.5">Affecte :</div>
                      {c.affects.slice(0, 6).map((a, j) => (
                        <div key={j} className="flex items-center gap-1.5 py-0.5">
                          <span className={`text-[8px] px-1 py-0.5 rounded border ${CLS[a.classification]?.cls || ""}`}>{CLS[a.classification]?.label}</span>
                          <button onClick={() => onOpenNode?.(a.node_id)} className="text-zinc-300 hover:text-white hover:underline truncate">{a.label}</button>
                          <span className="text-zinc-600">({a.relation})</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={() => onOpenNode?.(c.node_id)} data-testid={`temporal-diff-open-node-${i}`}
                    className="text-[#EF9F27] hover:underline">Ouvrir la fiche du nœud →</button>
                </div>
              )}
            </div>
          ))}
          {(data.changes || []).length === 0 && (
            <div className="p-4 text-center text-[11px] text-zinc-500" data-testid="temporal-diff-empty">Aucun changement de nœud sur la période.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemporalDiffPanel;
